/**
 * One card's face. Three kinds, one silhouette.
 *
 * Every card is the same height and the same two lines - a title and a quiet
 * second line - because the stack behind it only works if the cards are
 * interchangeable rectangles. A card that grew to fit its content would make
 * the fan-out arithmetic guesswork and the collapsed peek ragged.
 *
 * The meter is drawn INSIDE the card along its bottom edge rather than under
 * it, for the same reason: progress must not change how tall a card is.
 */
import {
  Activity,
  AlertTriangle,
  Brain,
  Building2,
  Check,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Radar,
  Send,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@wa/ui";
import { bgKey, type BgBeat, type BgEngine, type BgJob } from "../../../lib/background";
import { datasetLabel } from "../../../lib/datasetLabel";
import { fmtNum } from "../../ui/format";
import { weakestBeat, type BgCard } from "./cards";

type T = (key: string, vars?: Record<string, string | number>) => string;

/**
 * ⚠ TYPED AS A COMPLETE RECORD, NOT `as const`, AND THAT IS THE FIX.
 *
 * These two maps are indexed with a value the SERVER chose, and a miss does not
 * fall back to anything - it puts `undefined` in the element position, which
 * React answers with error #130 and a blank screen for the whole shell. The
 * rail is on every page, so one unlisted kind takes the entire CRM down.
 *
 * That is not a hypothetical: the development brain shipped as a seventh job
 * kind on 2026-08-17 and this map still had six. `as const` let it through
 * because the client's own union had not been widened either, so the two
 * omissions covered for each other and `tsc` had nothing to complain about.
 * `Record<BgJob["kind"], LucideIcon>` makes the NEXT one a build failure, and
 * `background.test.ts` catches the half that lives on the other side of the
 * HTTP boundary.
 */
const JOB_ICON: Record<BgJob["kind"], LucideIcon> = {
  campaign: Send,
  scanner: Radar,
  bulk: Radar,
  register: Building2,
  calls: Phone,
  geocode: MapPin,
  brain: Brain,
};

const ENGINE_ICON: Record<BgEngine, LucideIcon> = {
  scanner: Radar,
  calls: Phone,
  mail: Mail,
  register: Building2,
  // The same pulse glyph as the Saidiseire nav row.
  monitoring: Activity,
  // Saidiseire's slow jobs: the security scan leads them.
  monitoringJobs: ShieldCheck,
};

/** "11:04" - the only part of a timestamp that fits, and the only part wanted. */
function clock(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("et-EE", { hour: "2-digit", minute: "2-digit" });
}

/**
 * A running job's title, and the longer form that fits in a tooltip.
 *
 * Every kind but one arrives carrying a name meant to be read: a campaign's is
 * what the operator typed into it, and the three that have no name of their own
 * send an empty string and are captioned by their kind. The register is the
 * exception - its "name" is the importer's own dataset key, so a nightly import
 * captioned the card `general` in the middle of an Estonian interface, beside a
 * meter and a spinner that were otherwise fully translated.
 *
 * The dataset is the part worth the 244px, not the word "register" - the
 * building icon beside it already says that much, and every register run would
 * otherwise look identical to every other one. The kind goes into the tooltip
 * instead, where it costs nothing and survives the ellipsis.
 */
function jobTitle(job: BgJob, t: T): { text: string; hint?: string } {
  if (job.kind === "register") {
    const ds = datasetLabel(t, job.name);
    if (!ds) return { text: t("jobs.kind.register") };
    return { text: ds, hint: t("jobs.kind.registerDs", { ds }) };
  }
  return { text: job.name || t(`jobs.kind.${job.kind}`) };
}

/**
 * The pulse card's second line.
 *
 * When something is late it names what. Otherwise it reports the engine
 * furthest from having reported, with the time it last ran - which is the
 * question the card exists to answer, and the one it got wrong first: it used
 * to show the freshest pulse, which is always the calls worker ticking every
 * minute and tells nobody anything.
 */
function beatsLine(beats: BgBeat[], late: BgBeat[], t: T): string {
  if (late.length) {
    return late.map((b) => t(`jobs.engine.${b.key}`)).join(", ");
  }
  const weakest = weakestBeat(beats);
  if (weakest) {
    return t("jobs.beats.last", { engine: t(`jobs.engine.${weakest.key}`), when: clock(weakest.at) });
  }
  // Enabled but never yet measured: honest, and not an alarm.
  return beats.some((b) => b.enabled) ? t("jobs.beats.pending") : t("jobs.beats.none");
}

export function JobFace({ card, t }: { card: BgCard; t: T }) {
  if (card.tone === "alert") {
    const { alert } = card;
    return (
      <span className="crm-bgc crm-bgc-alert" title={alert.detail ?? undefined}>
        <span className="crm-bgc-i">
          <AlertTriangle className="size-4" strokeWidth={1.9} />
        </span>
        <span className="crm-bgc-txt">
          <b>{t(`jobs.alert.${bgKey(alert.id)}`)}</b>
          {/* THE TIME IS NEVER DROPPED, and it goes first.
              This line used to be `detail || since`, so an alert that had an
              engine's own words showed them INSTEAD of when it happened - and a
              failure that gave up at half past two last night then looked
              exactly like one happening right now. That is the difference
              between "act" and "already over", and it was the missing half.
              First rather than second because the line ellipsises: the detail
              can be cut, the answer to "is this still going on" cannot. */}
          <i>
            {alert.detail
              ? t("jobs.alert.sinceDetail", { when: clock(alert.at), detail: alert.detail })
              : t("jobs.alert.since", { when: clock(alert.at) })}
          </i>
        </span>
        {/* The count is a pill rather than part of the sentence. The rail is
            244px wide; "3 transcriptions failed" does not fit on one line and
            an ellipsis where the verb should be says nothing at all. */}
        {alert.count > 1 ? <span className="crm-bgc-n">{fmtNum(alert.count)}</span> : null}
      </span>
    );
  }

  if (card.tone === "job") {
    const { job } = card;
    const Icon = JOB_ICON[job.kind];
    const pct = job.total > 0 ? Math.min(100, Math.round((job.done / job.total) * 100)) : 0;
    const title = jobTitle(job, t);
    return (
      <span className="crm-bgc crm-bgc-job" title={title.hint}>
        <span className="crm-bgc-i">
          <Icon className="size-4" strokeWidth={1.9} />
        </span>
        <span className="crm-bgc-txt">
          <b>{title.text}</b>
          <i>
            {job.pending
              ? t("jobs.kind.waiting")
              : job.total > 0
                ? `${fmtNum(job.done)} / ${fmtNum(job.total)}`
                : t("jobs.kind.working")}
          </i>
        </span>
        {job.pending ? <span className="crm-bgc-n is-calm">{fmtNum(job.pending)}</span> : null}
        <Loader2 className="crm-bgc-spin size-3.5 animate-spin" />
        {job.total > 0 ? (
          <span className="crm-bgc-meter">
            <i style={{ width: `${pct}%` }} />
          </span>
        ) : null}
      </span>
    );
  }

  const late = card.late;
  return (
    <span className={cn("crm-bgc crm-bgc-beats", late.length && "is-late")}>
      <span className="crm-bgc-i">
        {late.length ? (
          <AlertTriangle className="size-4" strokeWidth={1.9} />
        ) : (
          <Check className="size-4" strokeWidth={2.2} />
        )}
      </span>
      <span className="crm-bgc-txt">
        <b>{late.length ? t("jobs.beats.lateTitle", { n: late.length }) : t("jobs.beats.title")}</b>
        <i>{beatsLine(card.beats, late, t)}</i>
      </span>
      <span className="crm-bgc-dots">
        {/* One line per engine has nowhere to go in a 244px rail, so each
            engine's own time lives on its icon instead of being lost. */}
        {card.beats.map((b) => {
          const Icon = ENGINE_ICON[b.key];
          const isLate = late.some((l) => l.key === b.key);
          const name = t(`jobs.engine.${b.key}`);
          const last = b.at
            ? t("jobs.beats.last", { engine: name, when: clock(b.at) })
            : `${name}: ${t(b.enabled ? "jobs.beats.pending" : "jobs.beats.off")}`;
          // A resting engine keeps its last-run time and gains the reason it
          // has not moved since. Without the reason the tooltip reads as an
          // outage with a timestamp attached, which is what it used to be.
          const when = b.resting ? `${last} · ${t("jobs.beats.resting")}` : last;
          return (
            <span key={b.key} title={when} aria-label={when} className="inline-flex">
              <Icon
                className={cn(
                  "size-3",
                  isLate ? "crm-bgc-dot-bad" : b.enabled ? "crm-bgc-dot-on" : "crm-bgc-dot-off",
                )}
              />
            </span>
          );
        })}
      </span>
    </span>
  );
}
